/* eslint-disable max-lines */

import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Signal,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormControlStatus,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';

import { filter, take } from 'rxjs';

import { emailValidator } from '@core/common-legacy';
import { InfoboxComponent } from '@core/components-infobox-legacy';
import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import {
  existingAppMemberValidator,
  existingTellentMemberValidator,
  TellentMembersHttpClient,
  // eslint-disable-next-line boundaries/element-types
} from '@core/tellent-data';
import {
  TellentAccessService,
  TellentAppsDataFacade,
  TellentMembersDataFacade,
} from '@core/tellent-logic';
import {
  BRAND,
  BulkUpdateMembersPayload,
  EMPLOYEE_ROLE,
  InviteExistingMemberForm,
  InviteMethod,
  SeatKind,
  SeatType,
  TellentBrandsDefaultRoles,
  TellentMember,
  TellentOrgAbilities,
  TellentOrgAbilitiesExtended,
  UserInvitationFlowData,
} from '@core/tellent-types';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogComponentI,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  DialogService,
  DialogWidth,
  IconComponent,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed, macroTask } from '@core/utils-legacy';

import { InviteExistingMemberDetailsFormComponent } from './invite-existing-member-details-form/invite-existing-member-details-form.component';
import { AccessRolesTableComponent } from '../access-roles-table/access-roles-table.component';
import { AccessRolesTableService } from '../access-roles-table/access-roles-table.service';
import { EditMembersActionsService } from '../edit-member-admin-dialog/edit-member-header/edit-members-actions.service';
import { EditMemberPermissionsBoxComponent } from '../edit-member-admin-dialog/edit-member-permissions-access-tab/edit-member-permissions-box/edit-member-permissions-box.component';
import { GiveAdminPermissionsDialogComponent } from '../give-admin-permissions-dialog/give-admin-permissions-dialog.component';

@Component({
  selector: 'tlc-invite-existing-member-dialog',
  templateUrl: './invite-existing-member-dialog.component.html',
  styleUrls: ['./invite-existing-member-dialog.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DialogHeaderComponent,
    TranslatePipe,
    DialogBodyComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    DialogFooterComponent,
    InviteExistingMemberDetailsFormComponent,
    ReactiveFormsModule,
    IconComponent,
    EditMemberPermissionsBoxComponent,
    AccessRolesTableComponent,
    InfoboxComponent,
  ],
})
export class InviteExistingMemberDialogComponent implements DialogComponentI<any, any> {
  private readonly membersDataFacade = inject(TellentMembersDataFacade);
  private readonly fb = inject(FormBuilder);
  private readonly dialogRef = inject(DialogRef);
  private readonly untilDestroyed = injectUntilDestroyed();
  private readonly t = injectTranslate();
  private readonly router = inject(Router);
  private readonly tellentAccessService = inject(TellentAccessService);
  private readonly tellentMembersHttpClient = inject(TellentMembersHttpClient);
  private readonly appsDataFacade = inject(TellentAppsDataFacade);
  private readonly dialogService = inject(DialogService);
  private readonly accessRolesTableSrv = inject(AccessRolesTableService);
  private readonly editMembersActionsService = inject(EditMembersActionsService);

  protected readonly InviteMethod = InviteMethod;

  public readonly brand = inject(BRAND);

  public formGroup: FormGroup<InviteExistingMemberForm>;
  public defaultRecruiteeRole = this.appsDataFacade.defaultRoleBySeatKind(SeatKind.Recruitment);
  public defaultKiwiRole = this.appsDataFacade.defaultRoleBySeatKind(SeatKind.CoreHR);
  public recruiteeApp = this.appsDataFacade.appBySeatKind(SeatKind.Recruitment);
  public kiwiApp = this.appsDataFacade.appBySeatKind(SeatKind.CoreHR);

  public memberData: Signal<TellentMember | null>;
  public canManageTellent: Signal<boolean> = this.tellentAccessService.canManageTellent;

  public loading$ = signal<boolean>(false);
  public displayAdvancedForm = signal<boolean>(false);
  public selectedAdminAbilities = signal<Array<TellentOrgAbilities | TellentOrgAbilitiesExtended>>(
    [],
  );

  public displayShowAdvancedButton = computed<boolean>(() => !this.memberData()?.hasWorkEmail);
  public showAdvancedButtonText = computed<string>(() =>
    this.displayAdvancedForm()
      ? this.t(
          'tlc.members.invite_existing_members.member_section.member_details_form.advanced.hide',
        )
      : this.t(
          'tlc.members.invite_existing_members.member_section.member_details_form.advanced.show',
        ),
  );

  public setDefaultRoles = effect(() => {
    const defaultRecruiteeRole = this.defaultRecruiteeRole();
    const defaultKiwiRole = this.defaultKiwiRole();

    if (defaultRecruiteeRole || defaultKiwiRole) {
      const defaults: TellentBrandsDefaultRoles = {
        recruitee: defaultRecruiteeRole,
        kiwi: defaultKiwiRole,
      };

      this.accessRolesTableSrv.applyDefaults(defaults, this.seats.recruitee, this.seats.kiwi);

      macroTask(() => this.formGroup.updateValueAndValidity());
    }
  });

  public get inviteMethodControl(): FormControl<InviteMethod | null> {
    return this.formGroup.controls.inviteMethod;
  }

  public get emailControl(): FormControl<string | null> {
    return this.formGroup.controls.email;
  }

  public get seats() {
    return this.formGroup.controls.seats.controls;
  }

  public setDialogData(data: any): void {
    this.memberData = this.membersDataFacade.teamMemberById(data.memberGuid);
  }

  public ngOnInit(): void {
    const member = this.memberData();

    this.formGroup = this.fb.group<InviteExistingMemberForm>({
      inviteMethod: this.fb.control(InviteMethod.Email),
      email: this.fb.control(
        { value: member?.hasWorkEmail ? member?.email : '', disabled: !!member?.hasWorkEmail },
        {
          validators: [Validators.required, emailValidator],
          asyncValidators: [
            this.tellentAccessService.canManageTellentUsers()
              ? existingTellentMemberValidator(this.tellentMembersHttpClient)
              : existingAppMemberValidator(this.tellentMembersHttpClient),
          ],
          updateOn: 'blur',
        },
      ),
      firstName: this.fb.control({ value: member?.firstName ?? '', disabled: true }),
      lastName: this.fb.control({ value: member?.lastName ?? '', disabled: true }),
      seats: this.fb.group({
        recruitee: this.createSeatFormGroup(),
        kiwi: this.createSeatFormGroup(),
      }),
    });

    this.listenToInviteMethodChanges();
  }

  public ngAfterViewInit(): void {
    this.accessRolesTableSrv.bindSeatLogic(SeatKind.Recruitment, this.seats.recruitee);
    this.accessRolesTableSrv.bindSeatLogic(SeatKind.CoreHR, this.seats.kiwi);

    this.assignGuidsToSeats();
  }

  public toggleAdvancedForm(): void {
    this.displayAdvancedForm.update(value => !value);
    if (!this.displayAdvancedForm()) this.handleHideAdvancedForm();
  }

  public navigateToCsvImport(): void {
    this.dialogRef.close();
    this.router.navigate(['team-members', 'import']);
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  public openGiveAdminPermissionsDialog(): void {
    this.dialogService
      .openDialog(
        GiveAdminPermissionsDialogComponent,
        { abilities: this.selectedAdminAbilities() },
        { width: DialogWidth.Small, containerOptions: { fullscreenOnMobile: true } },
      )
      .afterClosed.pipe(take(1), this.untilDestroyed)
      .subscribe(result => {
        if (result) this.selectedAdminAbilities.set(result.abilities);
      });
  }

  public submit(): void {
    this.loading$.set(true);

    this.formGroup.statusChanges
      .pipe(
        filter(status => status !== 'PENDING'),
        take(1),
        this.untilDestroyed,
      )
      .subscribe((status: FormControlStatus) => {
        if (status === 'INVALID') {
          this.loading$.set(false);
          this.formGroup.markAllAsTouched();
          this.formGroup.updateValueAndValidity();
          return;
        }

        if (status === 'VALID') {
          if (this.inviteMethodControl.value === InviteMethod.Email && this.shouldDisplayAutj()) {
            this.submitWithAutj();
          } else {
            this.submitWithoutAutj();
          }
        }
      });

    // Trigger validation if form is already valid/invalid (not pending)
    if (this.formGroup.status !== 'PENDING') {
      this.formGroup.updateValueAndValidity();
    }
  }

  private submitWithAutj(): void {
    const payload = this.prepareActionPayload();
    const member = this.memberData()!;
    const recruiteeRoleId = this.seats.recruitee.value.roleExternalId || '';
    const kiwiRoleId = this.seats.kiwi.value.roleExternalId || '';

    this.formGroup.markAsPristine();

    this.dialogRef.close({
      existingMemberPayload: payload,
      existingMemberDetails: {
        email: member.email ?? '',
        firstName: member.firstName ?? '',
        lastName: member.lastName ?? '',
      },
      inviteMethod: this.inviteMethodControl.value!,
      displayAutj: true,
      isExistingMember: true,
      recruiteeRoleId,
      kiwiRoleId,
    });
  }

  private submitWithoutAutj(): void {
    const payload: BulkUpdateMembersPayload = this.prepareActionPayload();

    this.editMembersActionsService.inviteExistingMember(payload);

    this.handleSubmitSuccess();
    this.handleSubmitFail();
  }

  private shouldDisplayAutj(): boolean {
    const selectedRoleExternalId = this.seats.recruitee.value.roleExternalId;
    if (!selectedRoleExternalId || !this.recruiteeApp()) return false;

    const selectedRoleAbilities = this.recruiteeApp()!.roles.find(
      role => role.externalId === selectedRoleExternalId,
    )?.abilities;

    if (!selectedRoleAbilities) return false;

    return !selectedRoleAbilities.some(
      ability => ability === TellentOrgAbilitiesExtended.RecruitmentViewAllJobs,
    );
  }

  private handleSubmitSuccess(): void {
    this.membersDataFacade.inviteExistingMemberSuccess$
      .pipe(take(1), this.untilDestroyed)
      .subscribe(({ payload }) => {
        this.formGroup.markAsPristine();
        const flowData: UserInvitationFlowData = {
          payload,
          inviteMethod: this.inviteMethodControl.value!,
        };
        this.dialogRef.close({ ...flowData });
      });
  }

  private handleSubmitFail(): void {
    this.loading$.set(false);
  }

  private handleHideAdvancedForm(): void {
    if (this.inviteMethodControl.value === InviteMethod.Login) {
      this.formGroup.patchValue({ inviteMethod: InviteMethod.Email });
      this.emailControl.reset('', { emitEvent: false });
    }
  }

  private createSeatFormGroup(): FormGroup<SeatType> {
    return this.fb.group<SeatType>({
      toggle: new FormControl<boolean | null>(null),
      appGuid: new FormControl<string | null>(''),
      roleExternalId: new FormControl<string | null>(''),
    });
  }

  private listenToInviteMethodChanges(): void {
    this.formGroup.controls.inviteMethod.valueChanges
      .pipe(this.untilDestroyed)
      .subscribe(inviteMethod => {
        switch (inviteMethod) {
          case InviteMethod.Email:
            this.formGroup.controls.seats.controls.recruitee.enable({ emitEvent: false });
            this.emailControl.enable();
            break;
          case InviteMethod.Login:
            this.adjustAccessAndRolesControls();
            this.emailControl.reset('', { emitEvent: false });
            this.emailControl.disable();
            this.formGroup.updateValueAndValidity();
            break;
          default:
            break;
        }
      });
  }

  // When adding a member via username and password, we need to ensure that Recruitee seat and Kiwi Administrator role are disabled.
  // Only Kiwi seat should be enabled with Employee role.
  // Also reset selected admin permissions.
  private adjustAccessAndRolesControls(): void {
    const kiwiValue = this.seats.kiwi.value;

    this.seats.recruitee.disable({ emitEvent: false });
    this.seats.recruitee.reset(
      { toggle: null, appGuid: '', roleExternalId: '' },
      { emitEvent: false },
    );
    this.seats.kiwi.reset({ ...kiwiValue, roleExternalId: EMPLOYEE_ROLE }, { emitEvent: false });

    this.selectedAdminAbilities.set([]);
  }

  private prepareActionPayload(): BulkUpdateMembersPayload {
    const { guid } = this.memberData()!;
    const { seats, inviteMethod } = this.formGroup.value;

    const payloadSeats = [
      { seat: seats?.kiwi, app: this.kiwiApp() },
      { seat: seats?.recruitee, app: this.recruiteeApp() },
    ]
      .filter(({ seat, app }) => seat?.toggle && app?.guid)
      .map(({ seat, app }) => ({
        access: seat!.toggle!,
        appGuid: app!.guid,
        roleExternalId: seat!.roleExternalId!,
      }));

    return <BulkUpdateMembersPayload>{
      members: [
        {
          guid,
          email: inviteMethod === InviteMethod.Email ? this.emailControl.getRawValue()! : undefined,
        },
      ],
      seats: payloadSeats,
      abilities: [...this.selectedAdminAbilities()],
      generateLogin: inviteMethod === InviteMethod.Login,
      sendInvitation: inviteMethod === InviteMethod.Email,
    };
  }

  private assignGuidsToSeats(): void {
    const kiwiGuid = this.kiwiApp()?.guid || '';
    const recruiteeGuid = this.recruiteeApp()?.guid || '';

    if (kiwiGuid) this.seats.kiwi.patchValue({ appGuid: kiwiGuid }, { emitEvent: false });
    if (recruiteeGuid)
      this.seats.recruitee.patchValue({ appGuid: recruiteeGuid }, { emitEvent: false });
  }
}
